import type { LandingPageContent } from '../types'

export const COSTE_INSTALACION_FOTOVOLTAICA: LandingPageContent = {
  slug: 'coste-instalacion-fotovoltaica',
  lang: 'es',
  topicId: 'solaranlage-kosten',
  title: 'Coste de una instalación fotovoltaica – comparación gratis',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica? Descubra los factores del precio y compare ' +
    'presupuestos de empresas instaladoras gratis.',
  keywords: [
    'coste instalación fotovoltaica',
    'precio placas solares',
    'cuánto cuesta una instalación solar',
    'precio instalación fotovoltaica',
    'calculadora coste fotovoltaica',
    'aire acondicionado fotovoltaica',
    'consumo pc gaming fotovoltaica',
  ],
  eyebrow: 'Guía de precios',
  h1: '¿Cuánto cuesta una instalación fotovoltaica? Precios y factores clave',
  intro:
    'El coste de una instalación fotovoltaica varía mucho: según el tamaño del tejado, la potencia, ' +
    'la batería y la región, puede haber varios miles de euros de diferencia entre dos presupuestos ' +
    'para el mismo tejado. Aquí descubrirá qué factores determinan realmente el precio y cómo ' +
    'encontrar un precio justo de mercado para su proyecto.',
  sections: [
    { type: 'heading', text: 'Por qué un precio único no sirve de mucho' },
    {
      type: 'paragraph',
      text:
        'Un precio fijo por kWp suena sencillo, pero ignora la forma del tejado, el andamiaje ' +
        'necesario, la elección de batería y los precios locales de las empresas instaladoras. El ' +
        'coste total surge de la combinación de varios factores - por eso los presupuestos para el ' +
        'mismo tejado suelen variar bastante en la práctica.',
    },
    { type: 'heading', text: 'Estos factores influyen en el precio' },
    {
      type: 'list',
      items: [
        'Tamaño de la instalación (kWp) y superficie de tejado disponible',
        'Orientación e inclinación del tejado',
        'Batería sí/no y su capacidad',
        'Consumo adicional por bomba de calor o coche eléctrico',
        'Estado del cuadro eléctrico y trabajos eléctricos necesarios',
        'Complejidad del andamiaje y montaje según el tipo de tejado',
        'Diferencias regionales de precio entre empresas instaladoras',
      ],
    },
    { type: 'heading', text: 'Por qué los presupuestos para el mismo tejado pueden variar tanto' },
    {
      type: 'paragraph',
      text:
        'La carga de trabajo, el margen y la experiencia varían de una empresa a otra - quien solo ' +
        'pide un presupuesto no tiene ningún punto de comparación y le resulta difícil evaluar si el ' +
        'precio es justo. Solo con varios presupuestos para la misma solicitud se ve dónde está ' +
        'realmente el precio de mercado.',
    },
    { type: 'heading', text: 'Así encuentra el mejor precio - sin llamadas comerciales' },
    {
      type: 'paragraph',
      text:
        'En Roofora crea una solicitud anónima: bastan unas fotos del tejado, una foto del cuadro ' +
        'eléctrico, la orientación y su consumo. Las empresas instaladoras responden con presupuestos ' +
        'a precio cerrado y sin compromiso, que usted compara con calma ordenados por precio. El ' +
        'contacto solo se produce cuando usted da su consentimiento activo a una empresa concreta - ' +
        'sin autorización colectiva, sin lluvia de llamadas.',
    },
  ],
  faq: [
    {
      q: '¿Merece la pena una batería en términos de precio?',
      a: 'Depende mucho de su perfil de consumo, por ejemplo si usa mucha electricidad durante el día o más bien por la noche. Las empresas instaladoras pueden valorarlo de forma realista a partir de sus datos de consumo, bomba de calor y coche eléctrico.',
    },
    {
      q: '¿Por qué varían tanto los presupuestos para el mismo tejado?',
      a: 'Porque la carga de trabajo, los materiales elegidos y el cálculo varían de una empresa a otra. Por eso merece la pena comparar varios presupuestos independientes en lugar de uno solo.',
    },
    {
      q: '¿Tiene algún coste usar Roofora?',
      a: 'No - crear una solicitud, recibir presupuestos y compararlos es completamente gratuito para usted como cliente.',
    },
    {
      q: '¿Mi instalación también es suficiente para un aire acondicionado en verano?',
      a: 'Los aires acondicionados suelen funcionar de día, justo cuando una instalación fotovoltaica genera más electricidad. Fuentes externas la señalan como una de las combinaciones económicamente más rentables. El tamaño necesario depende del tipo de equipo y su consumo restante - indique la necesidad adicional en la calculadora de dimensionamiento.',
    },
    {
      q: '¿Un PC gaming cambia algo en el tamaño de instalación recomendado?',
      a: 'Un PC gaming puede consumir varios cientos de vatios según el equipo, lo que se nota en el consumo anual con un uso prolongado. Roofora no calcula un recargo propio para ello - indique su consumo anual real, incluyendo el uso gaming, en la calculadora de dimensionamiento para que el tamaño de instalación se ajuste.',
    },
    {
      q: '¿Puedo usar el excedente fotovoltaico para minería de criptomonedas?',
      a: 'Técnicamente sí - el excedente de electricidad solar, que de otro modo se vertería a la red por poco dinero, puede usarse para hardware de minería. Es un caso especial que no calculamos en nuestra herramienta - la rentabilidad depende mucho de su hardware y de las condiciones actuales, y debería calcularse por separado.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Cómo funciona la comparación de presupuestos',
    action: 'link',
    href: '/comparar-presupuestos-fotovoltaicos',
  },
  relatedLinks: [
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
    { label: 'Encontrar instalador fotovoltaico', href: '/encontrar-instalador-fotovoltaico' },
    { label: 'Planificar la instalación paso a paso', href: '/planificar-instalacion-fotovoltaica' },
    { label: 'Fotovoltaica y bomba de calor combinadas', href: '/fotovoltaica-bomba-de-calor' },
    { label: 'Wallbox y coche eléctrico con excedente solar', href: '/fotovoltaica-wallbox-coche-electrico' },
    { label: 'Ayudas para fotovoltaica: KfW, tarifa y subvenciones', href: '/ayudas-fotovoltaica' },
  ],
}
